import type { CSSProperties } from 'react';

type AssistantMotionDurationKey =
  | 'instant'
  | 'hover'
  | 'micro'
  | 'panel'
  | 'route-enter'
  | 'route-exit'
  | 'emphasis';

type AssistantMotionEasingKey = 'enter' | 'exit' | 'standard' | 'view' | 'linear';
type AssistantMotionSpringKey = 'gesture' | 'interruptible' | 'layout';
export type AssistantMotionPresetKey = 'shell' | 'panel' | 'disclosure' | 'micro' | 'emphasis';

type AssistantMotionSpring = {
  stiffness: number;
  damping: number;
  mass: number;
};

type AssistantMotionTokenSnapshot = {
  durations: Record<AssistantMotionDurationKey, string>;
  easing: Record<AssistantMotionEasingKey, string>;
  distance: {
    micro: string;
    panel: string;
    route: string;
  };
  springs: Record<AssistantMotionSpringKey, AssistantMotionSpring>;
  spring: Record<AssistantMotionSpringKey, AssistantMotionSpring>;
  reducedMotion: {
    transformDuration: string;
    opacityDuration: string;
    disableScale: boolean;
    disableStagger: boolean;
  };
  viewTransitions: {
    nameRequired: boolean;
    uniquePerElement: boolean;
    cleanupRequired: boolean;
    nativeScope: string;
  };
  icons: {
    viewBox: number;
    lineCount: number;
    lineCap: string;
    groupRotation: string;
    nonGroupRotation: string;
    reducedMotion: string;
  };
  container: {
    twoDivPattern: boolean;
    measurement: string;
    initialFallback: string;
    overflowHidden: boolean;
    delay: string;
  };
};

type AssistantMotionPreset = {
  durationMs: number;
  cssEasing: string;
  motionEase: 'linear' | [number, number, number, number];
};

const parseDurationMs = (value: string): number => Number.parseFloat(value.replace('ms', ''));

const parseMotionEase = (value: string): AssistantMotionPreset['motionEase'] => {
  if (value === 'linear') {
    return 'linear';
  }

  if (value === 'ease-in-out') {
    return [0.42, 0, 0.58, 1];
  }

  const bezierMatch = value.match(/cubic-bezier\(([^)]+)\)/i);
  if (!bezierMatch) {
    return [0.2, 0, 0, 1];
  }

  const points = bezierMatch[1]
    .split(',')
    .map((point) => Number.parseFloat(point.trim()))
    .filter((point) => Number.isFinite(point));

  if (points.length !== 4) {
    return [0.2, 0, 0, 1];
  }

  return [points[0], points[1], points[2], points[3]];
};

export const ASSISTANT_MOTION_TOKEN_SNAPSHOT: AssistantMotionTokenSnapshot = {
  durations: {
    instant: '0ms',
    hover: '140ms',
    micro: '180ms',
    panel: '220ms',
    'route-enter': '180ms',
    'route-exit': '120ms',
    emphasis: '280ms',
  },
  easing: {
    enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
    exit: 'cubic-bezier(0.32, 0, 0.67, 0)',
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    view: 'ease-in-out',
    linear: 'linear',
  },
  distance: {
    micro: '4px',
    panel: '12px',
    route: '16px',
  },
  springs: {
    gesture: {
      stiffness: 420,
      damping: 34,
      mass: 0.8,
    },
    interruptible: {
      stiffness: 360,
      damping: 32,
      mass: 0.9,
    },
    layout: {
      stiffness: 300,
      damping: 30,
      mass: 1,
    },
  },
  spring: {
    gesture: {
      stiffness: 420,
      damping: 34,
      mass: 0.8,
    },
    interruptible: {
      stiffness: 360,
      damping: 32,
      mass: 0.9,
    },
    layout: {
      stiffness: 300,
      damping: 30,
      mass: 1,
    },
  },
  reducedMotion: {
    transformDuration: '0ms',
    opacityDuration: '120ms',
    disableScale: true,
    disableStagger: true,
  },
  viewTransitions: {
    nameRequired: true,
    uniquePerElement: true,
    cleanupRequired: true,
    nativeScope: 'progressive-route-polish',
  },
  icons: {
    viewBox: 14,
    lineCount: 3,
    lineCap: 'round',
    groupRotation: 'spring',
    nonGroupRotation: 'jump',
    reducedMotion: 'disableAnimation',
  },
  container: {
    twoDivPattern: true,
    measurement: 'ResizeObserver',
    initialFallback: 'auto',
    overflowHidden: true,
    delay: '50ms',
  },
};

const createPreset = (
  durationKey: AssistantMotionDurationKey,
  easingKey: AssistantMotionEasingKey,
): AssistantMotionPreset => {
  const cssEasing = ASSISTANT_MOTION_TOKEN_SNAPSHOT.easing[easingKey];
  return {
    durationMs: parseDurationMs(ASSISTANT_MOTION_TOKEN_SNAPSHOT.durations[durationKey]),
    cssEasing,
    motionEase: parseMotionEase(cssEasing),
  };
};

const ASSISTANT_MOTION_PRESET_MAP: Record<AssistantMotionPresetKey, AssistantMotionPreset> = {
  shell: createPreset('panel', 'enter'),
  panel: createPreset('panel', 'enter'),
  disclosure: createPreset('panel', 'enter'),
  micro: createPreset('micro', 'standard'),
  emphasis: createPreset('emphasis', 'enter'),
};

const reducedMotionPreset = (): AssistantMotionPreset => ({
  durationMs: parseDurationMs(ASSISTANT_MOTION_TOKEN_SNAPSHOT.reducedMotion.opacityDuration),
  cssEasing: ASSISTANT_MOTION_TOKEN_SNAPSHOT.easing.standard,
  motionEase: parseMotionEase(ASSISTANT_MOTION_TOKEN_SNAPSHOT.easing.standard),
});

export const resolveAssistantMotionPreset = (
  key: AssistantMotionPresetKey,
  reducedMotion = false,
): AssistantMotionPreset => {
  const preset = ASSISTANT_MOTION_PRESET_MAP[key];
  return reducedMotion ? reducedMotionPreset() : preset;
};

export const resolveAssistantSpring = (
  key: AssistantMotionSpringKey,
): AssistantMotionSpring => ASSISTANT_MOTION_TOKEN_SNAPSHOT.springs[key];

export const getAssistantTransitionStyle = (
  key: AssistantMotionPresetKey,
  reducedMotion = false,
  transitionProperty = [
    'transform',
    'opacity',
    'background-color',
    'border-color',
    'box-shadow',
    'color',
  ],
): CSSProperties => {
  const preset = resolveAssistantMotionPreset(key, reducedMotion);
  return {
    transitionProperty: transitionProperty.join(', '),
    transitionDuration: `${preset.durationMs}ms`,
    transitionTimingFunction: preset.cssEasing,
  };
};

export const getAssistantMotionTransition = (
  key: AssistantMotionPresetKey,
  reducedMotion = false,
): { duration: number; ease: AssistantMotionPreset['motionEase'] } => {
  const preset = resolveAssistantMotionPreset(key, reducedMotion);
  return {
    duration: preset.durationMs / 1000,
    ease: preset.motionEase,
  };
};
